import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { toast } from '../utils/toast';
import { Breadcrumbs, BreadcrumbItem } from '../components/ui/Breadcrumbs';
import { DataTable, ExportButton } from '../components/ui';
import type { Column } from '../components/ui/DataTable';
import type { Wallet, WalletTransaction } from '../types';
import { TransactionReceiptModal } from '../components/TransactionReceiptModal';
import {
  CreditCard,
  Landmark,
  Smartphone,
  Wallet as WalletIcon,
  Plus,
  ArrowUpRight,
  ArrowDownLeft,
  CheckCircle2,
  FileText,
  FileSpreadsheet,
  Calendar,
  X,
  Filter,
} from 'lucide-react';

export const WalletPage: React.FC = () => {
  const queryClient = useQueryClient();
  const client = useAuthStore((state) => state.client);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<
    'credit_card' | 'bank_transfer' | 'mobile_money'
  >('credit_card');

  // Receipt & Statement Modals State
  const [selectedReceiptTransaction, setSelectedReceiptTransaction] =
    useState<WalletTransaction | null>(null);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [statementMonth, setStatementMonth] = useState<string>(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  });

  // Filters state
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    start_date: '',
    end_date: '',
    search: '',
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [limit, setLimit] = useState(20);

  // Close modals on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowTopUpModal(false);
        setTopUpAmount('');
        setShowStatementModal(false);
        setSelectedReceiptTransaction(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
      setOffset(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Reset offset when filters change
  useEffect(() => {
    setOffset(0);
  }, [filters.type, filters.status, filters.start_date, filters.end_date, limit]);

  const { data: wallet, isLoading: walletLoading } = useQuery({
    queryKey: ['wallet', client?.id],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: Wallet }>(
        `/wallet/balance/${client?.id}`
      );
      return response.data.data;
    },
    enabled: !!client?.id,
  });

  const currency = wallet?.currency || 'AOA';
  const formatAmount = (val: number) => {
    return `${Number(val || 0).toLocaleString('pt-AO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currency}`;
  };

  const { data: transactionsData, isLoading: transactionsLoading } = useQuery({
    queryKey: [
      'transactions',
      client?.id,
      filters.type,
      filters.status,
      filters.start_date,
      filters.end_date,
      debouncedSearch,
      offset,
      limit,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (debouncedSearch) params.append('search', debouncedSearch);
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      const response = await api.get<{
        success: boolean;
        data: WalletTransaction[];
        meta: { total: number; limit: number; offset: number };
      }>(`/wallet/transactions/${client?.id}?${params.toString()}`);
      return response.data;
    },
    enabled: !!client?.id,
  });

  const transactions = transactionsData?.data || [];
  const total = transactionsData?.meta?.total || 0;

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      status: '',
      start_date: '',
      end_date: '',
      search: '',
    });
    setOffset(0);
  };

  const hasActiveFilters = Object.values(filters).some((v) => v !== '');

  const topUpMutation = useMutation({
    mutationFn: async (data: { amount: number; payment_method: string }) => {
      const response = await api.post('/wallet/topup', {
        ...data,
        client_id: client?.id,
      });
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['wallet'] });
      void queryClient.invalidateQueries({ queryKey: ['transactions'] });
      setShowTopUpModal(false);
      setTopUpAmount('');
      toast.success('Fundos adicionados com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao adicionar fundos.');
    },
  });

  const handleTopUp = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = parseFloat(topUpAmount);

    if (amount < 10) {
      toast.warning('O valor mínimo de carregamento é $10');
      return;
    }

    if (amount > 10000) {
      toast.warning('O valor máximo de carregamento é $10.000');
      return;
    }

    topUpMutation.mutate({ amount, payment_method: paymentMethod });
  };

  // Statement Analytics
  const statementTransactions = useMemo(() => {
    return transactions.filter((t) => {
      if (!t.created_at) return false;
      return t.created_at.startsWith(statementMonth);
    });
  }, [transactions, statementMonth]);

  const statementCredits = useMemo(() => {
    return statementTransactions
      .filter((t) => t.type === 'credit' && t.status === 'completed')
      .reduce((acc, t) => acc + (Number(t.amount) || 0), 0);
  }, [statementTransactions]);

  const statementDebits = useMemo(() => {
    return statementTransactions
      .filter((t) => t.type === 'debit' && t.status === 'completed')
      .reduce((acc, t) => acc + (Number(t.amount) || 0), 0);
  }, [statementTransactions]);

  const netStatementPeriod = statementCredits - statementDebits;

  const statementExportHeaders = [
    { key: 'created_at', label: 'Data & Hora' },
    { key: 'id', label: 'ID Transação' },
    { key: 'type', label: 'Natureza (Crédito/Débito)' },
    { key: 'description', label: 'Descrição' },
    { key: 'amount', label: `Valor (${currency})` },
    { key: 'status', label: 'Estado' },
  ];

  const columns: Column<WalletTransaction>[] = [
    {
      key: 'created_at',
      header: 'Data',
      render: (t) => (
        <span className="text-xs text-slate-600 dark:text-slate-400 font-mono">
          {t.created_at ? new Date(t.created_at).toLocaleString('pt-BR') : '-'}
        </span>
      ),
    },
    {
      key: 'type',
      header: 'Tipo',
      render: (t) => (
        <span
          className={`badge-modern text-xs ${t.type === 'credit' ? 'badge-success' : 'badge-primary'}`}
        >
          {t.type === 'credit' ? 'Crédito' : 'Débito'}
        </span>
      ),
    },
    {
      key: 'description',
      header: 'Descrição',
      render: (t) => (
        <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">
          {t.description}
        </span>
      ),
    },
    {
      key: 'amount',
      header: 'Valor',
      render: (t) => (
        <span
          className={`font-mono font-bold text-xs inline-flex items-center gap-1 ${
            t.type === 'credit'
              ? 'text-emerald-600 dark:text-emerald-400'
              : 'text-rose-600 dark:text-rose-400'
          }`}
        >
          {t.type === 'credit' ? (
            <ArrowDownLeft className="w-3.5 h-3.5 text-emerald-500" />
          ) : (
            <ArrowUpRight className="w-3.5 h-3.5 text-rose-500" />
          )}
          {t.type === 'credit' ? '+' : '-'}
          {formatAmount(t.amount)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      render: (t) => (
        <span
          className={`badge-modern text-xs ${
            t.status === 'completed'
              ? 'badge-success'
              : t.status === 'pending'
                ? 'badge-warning'
                : 'badge-error'
          }`}
        >
          {t.status === 'completed' ? 'Completo' : t.status === 'pending' ? 'Pendente' : 'Falhado'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Comprovativo',
      render: (t) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSelectedReceiptTransaction(t);
          }}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 transition-colors"
          title="Ver Comprovativo / Fatura-Recibo Oficial"
        >
          <FileText className="w-3.5 h-3.5 text-primary-500" />
          <span>Recibo</span>
        </button>
      ),
    },
  ];

  const exportHeaders = [
    { key: 'created_at', label: 'Data' },
    { key: 'type', label: 'Tipo' },
    { key: 'description', label: 'Descrição' },
    { key: 'amount', label: `Valor (${currency})` },
    { key: 'status', label: 'Estado' },
  ];

  return (
    <div className="container-custom py-8 animate-fade-in space-y-6">
      {/* Breadcrumbs */}
      <Breadcrumbs>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem>Carteira</BreadcrumbItem>
      </Breadcrumbs>

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
            <WalletIcon className="w-8 h-8 text-primary-500" />
            <span>Carteira & Faturação</span>
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Gira o saldo pré-pago, emita extratos contabilísticos e consulte comprovativos fiscais
            de transações
          </p>
        </div>

        {/* Global Statement Hub CTA */}
        <button
          type="button"
          onClick={() => setShowStatementModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 text-xs font-bold shadow-xs transition-colors"
        >
          <FileSpreadsheet className="w-4 h-4 text-emerald-500" />
          <span>Extrato Contabilístico Mensal</span>
        </button>
      </div>

      {/* Balance Card */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200/80 dark:border-slate-800 shadow-xs">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
          <div className="flex items-start gap-4 flex-1">
            <div className="p-3.5 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 shrink-0">
              <WalletIcon className="w-7 h-7" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Saldo Disponível
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                  <CheckCircle2 className="w-3 h-3" /> Ativa
                </span>
              </div>
              {walletLoading ? (
                <div className="h-10 w-40 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800 mt-2" />
              ) : (
                <p className="text-3xl font-extrabold text-slate-900 dark:text-white font-mono mt-1">
                  {formatAmount(wallet?.balance || 0)}
                </p>
              )}
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                Última atualização:{' '}
                {wallet?.updated_at ? new Date(wallet.updated_at).toLocaleString('pt-PT') : 'Hoje'}
              </p>
            </div>
          </div>

          {/* Quick Top-Up and Action */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
            <div className="flex items-center gap-1.5 p-1 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-semibold">
              {[5000, 10000, 25000, 50000].map((amt) => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => {
                    setTopUpAmount(String(amt));
                    setShowTopUpModal(true);
                  }}
                  className="px-2.5 py-1.5 rounded-lg text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-white/60 dark:hover:bg-slate-700 transition-all font-mono"
                >
                  +{amt.toLocaleString('pt-AO')}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setShowTopUpModal(true)}
              className="btn-base btn-primary flex items-center justify-center gap-2 text-xs font-semibold px-4 py-2.5 rounded-xl shadow-xs hover:shadow-md"
            >
              <Plus className="w-4 h-4" />
              <span>Adicionar Fundos</span>
            </button>
          </div>
        </div>
      </div>

      {/* Top-Up Modal */}
      {showTopUpModal && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-md w-full p-6 border border-slate-200 dark:border-slate-800 shadow-2xl space-y-6">
            <div className="flex justify-between items-center pb-4 border-b border-slate-100 dark:border-slate-800">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-primary-500" />
                <span>Carregar Saldo da Carteira</span>
              </h2>
              <button
                onClick={() => setShowTopUpModal(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleTopUp} className="space-y-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Valor a Carregar ({currency})
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400">
                    $
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="10"
                    max="10000"
                    required
                    value={topUpAmount}
                    onChange={(e) => setTopUpAmount(e.target.value)}
                    placeholder="100.00"
                    className="w-full pl-8 pr-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white text-base font-mono font-bold focus:ring-2 focus:ring-primary-500 outline-hidden"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Método de Pagamento
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('credit_card')}
                    className={`p-3 rounded-xl border text-center transition-all flex flex-col items-center gap-1.5 ${
                      paymentMethod === 'credit_card'
                        ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-950/40 text-primary-600 dark:text-primary-400 font-bold'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <CreditCard className="w-5 h-5" />
                    <span className="text-2xs">Cartão EMIS</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('bank_transfer')}
                    className={`p-3 rounded-xl border text-center transition-all flex flex-col items-center gap-1.5 ${
                      paymentMethod === 'bank_transfer'
                        ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-950/40 text-primary-600 dark:text-primary-400 font-bold'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <Landmark className="w-5 h-5" />
                    <span className="text-2xs">Transf. IBAN</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('mobile_money')}
                    className={`p-3 rounded-xl border text-center transition-all flex flex-col items-center gap-1.5 ${
                      paymentMethod === 'mobile_money'
                        ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-950/40 text-primary-600 dark:text-primary-400 font-bold'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <Smartphone className="w-5 h-5" />
                    <span className="text-2xs">Multicaixa Expr.</span>
                  </button>
                </div>
              </div>

              <div className="pt-2 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowTopUpModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={topUpMutation.isPending || !topUpAmount}
                  className="btn-base btn-primary px-5 py-2 text-xs font-bold shadow-xs disabled:opacity-50"
                >
                  {topUpMutation.isPending ? 'A Processar...' : 'Confirmar Carregamento'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Monthly Statement Hub Modal */}
      {showStatementModal && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-3xl w-full p-6 sm:p-8 border border-slate-200 dark:border-slate-800 shadow-2xl space-y-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start pb-4 border-b border-slate-100 dark:border-slate-800">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <FileSpreadsheet className="w-6 h-6 text-emerald-500" />
                  <span>Extrato Mensal & Relatório Contabilístico</span>
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Extrato oficial discriminado para fins fiscais e reconciliação financeira
                </p>
              </div>
              <button
                onClick={() => setShowStatementModal(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Month Selector */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-slate-50 dark:bg-slate-800/40 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2.5">
                <Calendar className="w-4 h-4 text-primary-500" />
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Selecionar Período Fiscal:
                </span>
                <input
                  type="month"
                  value={statementMonth}
                  onChange={(e) => setStatementMonth(e.target.value)}
                  className="px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-xs font-mono font-bold focus:ring-2 focus:ring-primary-500 outline-hidden"
                />
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <ExportButton
                  data={statementTransactions}
                  filename={`extrato_gateway_${statementMonth}.csv`}
                  headers={statementExportHeaders}
                />
              </div>
            </div>

            {/* Accounting Breakdown Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-4 rounded-xl bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200/80 dark:border-emerald-800/50">
                <span className="text-2xs font-bold text-emerald-800 dark:text-emerald-400 uppercase tracking-wider block mb-1">
                  Total Carregado (+)
                </span>
                <span className="text-lg font-extrabold text-emerald-700 dark:text-emerald-300 font-mono">
                  {formatAmount(statementCredits)}
                </span>
              </div>

              <div className="p-4 rounded-xl bg-rose-50/60 dark:bg-rose-950/20 border border-rose-200/80 dark:border-rose-800/50">
                <span className="text-2xs font-bold text-rose-800 dark:text-rose-400 uppercase tracking-wider block mb-1">
                  Total Consumido (-)
                </span>
                <span className="text-lg font-extrabold text-rose-700 dark:text-rose-300 font-mono">
                  {formatAmount(statementDebits)}
                </span>
              </div>

              <div className="p-4 rounded-xl bg-slate-100/70 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                <span className="text-2xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider block mb-1">
                  Saldo Líquido do Mês
                </span>
                <span
                  className={`text-lg font-extrabold font-mono ${
                    netStatementPeriod >= 0
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-rose-600 dark:text-rose-400'
                  }`}
                >
                  {netStatementPeriod >= 0 ? '+' : ''}
                  {formatAmount(netStatementPeriod)}
                </span>
              </div>
            </div>

            {/* Statement Transactions List */}
            <div className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
              <div className="max-h-60 overflow-y-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 font-bold uppercase text-2xs sticky top-0">
                    <tr>
                      <th className="p-3">Data</th>
                      <th className="p-3">Descrição</th>
                      <th className="p-3 text-right">Valor</th>
                      <th className="p-3 text-center">Ação</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-mono">
                    {statementTransactions.length > 0 ? (
                      statementTransactions.map((t) => (
                        <tr
                          key={t.id}
                          className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                        >
                          <td className="p-3 text-slate-500 text-2xs">
                            {new Date(t.created_at).toLocaleDateString('pt-PT')}
                          </td>
                          <td className="p-3 font-sans font-medium text-slate-800 dark:text-slate-200">
                            {t.description}
                          </td>
                          <td
                            className={`p-3 text-right font-bold ${
                              t.type === 'credit'
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : 'text-rose-600 dark:text-rose-400'
                            }`}
                          >
                            {t.type === 'credit' ? '+' : '-'}
                            {formatAmount(t.amount)}
                          </td>
                          <td className="p-3 text-center">
                            <button
                              onClick={() => {
                                setSelectedReceiptTransaction(t);
                              }}
                              className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline"
                            >
                              Ver Recibo
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="p-6 text-center text-slate-400 font-sans">
                          Nenhuma movimentação registada para {statementMonth}.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => setShowStatementModal(false)}
                className="px-4 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 text-xs font-bold rounded-xl transition-colors"
              >
                Fechar Extrato
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Official Transaction Receipt Modal */}
      <TransactionReceiptModal
        isOpen={!!selectedReceiptTransaction}
        onClose={() => setSelectedReceiptTransaction(null)}
        transaction={selectedReceiptTransaction}
        clientName={client?.company_name}
        clientTaxId={client?.tax_id}
        clientEmail={client?.email}
        currency={currency}
      />

      {/* Transactions Section */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Histórico de Movimentações
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Consulte os carregamentos e débitos efetuados pelas aplicações da sua conta
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              <Filter className="w-3.5 h-3.5 text-primary-500" />
              <span>{showFilters ? 'Ocultar Filtros' : 'Filtros'}</span>
            </button>
            <ExportButton
              data={transactions}
              filename={`transacoes_${Date.now()}.csv`}
              headers={exportHeaders}
            />
          </div>
        </div>

        {/* Filter Drawer */}
        {showFilters && (
          <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <input
                type="text"
                placeholder="Pesquisar descrição..."
                className="input-modern text-xs"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
              <select
                className="input-modern text-xs"
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
              >
                <option value="">Todos os Tipos</option>
                <option value="credit">Crédito (+)</option>
                <option value="debit">Débito (-)</option>
              </select>
              <select
                className="input-modern text-xs"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">Todos os Estados</option>
                <option value="completed">Completo</option>
                <option value="pending">Pendente</option>
                <option value="failed">Falhado</option>
              </select>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  className="input-modern text-xs w-full"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                />
                <span className="text-slate-400 text-xs">até</span>
                <input
                  type="date"
                  className="input-modern text-xs w-full"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                />
              </div>
            </div>
            {hasActiveFilters && (
              <div className="flex justify-end pt-1">
                <button
                  onClick={clearFilters}
                  className="text-xs text-rose-600 dark:text-rose-400 hover:underline font-semibold"
                >
                  Limpar todos os filtros
                </button>
              </div>
            )}
          </div>
        )}

        {/* Table */}
        <DataTable
          columns={columns}
          data={transactions}
          isLoading={transactionsLoading}
          total={total}
          limit={limit}
          offset={offset}
          onPageChange={setOffset}
          onLimitChange={setLimit}
          onRowClick={(t) => setSelectedReceiptTransaction(t)}
          emptyTitle={hasActiveFilters ? 'Nenhuma transação encontrada' : 'Nenhuma transação ainda'}
          emptyDescription={
            hasActiveFilters
              ? 'Tente ajustar os filtros de pesquisa.'
              : 'Quando adicionar fundos ou utilizar as APIs, as transações aparecerão aqui.'
          }
        />
      </div>
    </div>
  );
};

export default WalletPage;
